<p-confirmPopup />

<p-card header="Sincronizar Usuarios">
  <p-tabView>

    <!-- ════════ Tab 1: Configuraciones ════════ -->
    <p-tabPanel header="Configuraciones">

      <!-- Configuración de conexión JDE / Corpaceros -->
      <div class="mb-2">

        <!-- Loading -->
        <div *ngIf="configLoading" class="flex justify-content-center p-4">
          <p-progressSpinner strokeWidth="4" [style]="{ width: '32px', height: '32px' }" />
        </div>

        <!-- Estado vacío: sin configuración -->
        <div *ngIf="!configLoading && !showConfigForm && !config"
          class="flex flex-column align-items-center justify-content-center p-5 text-center border-1 surface-border border-round">
          <i class="pi pi-link text-5xl text-300 mb-3"></i>
          <h4 class="text-600 mt-0 mb-2">Sin configuración de conexión</h4>
          <p class="text-400 mb-3 max-w-20rem">
            Configura la conexión con JDE/Corpaceros antes de sincronizar usuarios.
          </p>
          <p-button label="Nueva integración" icon="pi pi-plus" (click)="openNewConfig()" />
        </div>

        <!-- Tarjeta resumen: ya hay configuración -->
        <p-card *ngIf="!configLoading && !showConfigForm && config" styleClass="shadow-1">
          <ng-template pTemplate="header">
            <div class="flex align-items-center justify-content-between px-3 pt-3 gap-2">
              <p-tag
                [severity]="config.isActive ? 'success' : 'secondary'"
                [value]="config.isActive ? 'Activa' : 'Inactiva'" />
            </div>
          </ng-template>

          <ng-template pTemplate="content">
            <div class="flex align-items-center justify-content-between gap-2 flex-wrap mb-1">
              <h3 class="text-900 font-bold m-0 line-clamp-1">{{ config.name }}</h3>
              <p-button *ngIf="status === 'inactivo'" label="Iniciar sincronización" icon="pi pi-sync"
                styleClass="p-button-sm" (click)="confirmStart($event)" />
            </div>
            <div class="text-xs font-mono text-400 white-space-nowrap overflow-hidden text-overflow-ellipsis"
              [pTooltip]="config.employeesApiUrl" tooltipPosition="bottom">
              {{ config.employeesApiUrl }}
            </div>
            <div class="text-xs text-400 mt-2 flex gap-3 flex-wrap">
              <span><i class="pi pi-clock mr-1"></i>Timeout: {{ config.timeoutSeconds }}s</span>
              <span><i class="pi pi-replay mr-1"></i>Reintentos: {{ config.maxRetries }}</span>
            </div>
          </ng-template>

          <ng-template pTemplate="footer">
            <div class="flex gap-2">
              <p-button icon="pi pi-pencil" label="Editar"
                styleClass="p-button-outlined p-button-sm flex-1"
                (click)="openEditConfig()" />
              <p-button icon="pi pi-trash"
                styleClass="p-button-outlined p-button-danger p-button-sm"
                (click)="confirmDeleteConfig($event)"
                pTooltip="Eliminar" />
            </div>

            <p-divider *ngIf="status !== 'inactivo'" styleClass="my-2" />

            <!-- Estado: Iniciando -->
            <div *ngIf="status === 'iniciando'" class="text-center py-4">
              <i class="pi pi-spin pi-spinner text-6xl text-blue-500 mb-3"></i>
              <p class="text-lg font-semibold mb-2">Conectando con Corpaceros...</p>
              <p class="text-sm text-600">Esto puede tardar unos segundos</p>
            </div>

            <!-- Estado: En progreso -->
            <div *ngIf="status === 'en-progreso'" class="py-3">
              <div class="mb-4">
                <div class="flex justify-content-between mb-2">
                  <span class="font-semibold">Progreso de sincronización</span>
                  <span class="font-bold text-primary">{{ progress }}%</span>
                </div>
                <p-progressBar [value]="progress" [showValue]="false" />
              </div>

              <div class="grid mb-3">
                <div class="col-6">
                  <div class="text-sm text-600">Bloque actual</div>
                  <div class="text-lg font-semibold">{{ currentBlock }} de {{ totalBlocks }}</div>
                </div>
                <div class="col-6">
                  <div class="text-sm text-600">Usuarios procesados</div>
                  <div class="text-lg font-semibold">{{ itemsProcessed }} / {{ totalItems }}</div>
                </div>
              </div>

              <div class="grid">
                <div class="col-4">
                  <div class="text-sm text-600">Creados</div>
                  <div class="text-lg font-semibold text-green-600">{{ usersCreated }}</div>
                </div>
                <div class="col-4">
                  <div class="text-sm text-600">Actualizados</div>
                  <div class="text-lg font-semibold text-blue-600">{{ usersUpdated }}</div>
                </div>
                <div class="col-4">
                  <div class="text-sm text-600">Errores</div>
                  <div class="text-lg font-semibold" [class.text-red-600]="errors > 0">{{ errors }}</div>
                </div>
              </div>

              <p-divider />

              <div class="flex align-items-center gap-2 text-sm text-600">
                <i class="pi pi-info-circle"></i>
                <span>No cierre esta ventana hasta que se complete la sincronización</span>
              </div>
            </div>

            <!-- Estado: Completado -->
            <div *ngIf="status === 'completado'" class="py-3">
              <div class="text-center mb-4">
                <i class="pi pi-check-circle text-6xl text-green-500 mb-3"></i>
                <p class="text-lg font-semibold mb-1">¡Sincronización completada!</p>
                <p class="text-sm text-600">Todos los usuarios han sido procesados correctamente</p>
              </div>

              <div class="surface-50 border-round p-3">
                <div class="grid">
                  <div class="col-6">
                    <div class="text-sm text-600">Total recibidos</div>
                    <div class="text-xl font-bold text-primary">{{ totalEmployeesReceived }}</div>
                  </div>
                  <div class="col-6">
                    <div class="text-sm text-600">Errores</div>
                    <div class="text-xl font-bold" [class.text-red-600]="errors > 0">{{ errors }}</div>
                  </div>
                </div>

                <p-divider />

                <div class="grid">
                  <div class="col-6">
                    <div class="text-sm text-600">Usuarios creados</div>
                    <div class="text-lg font-semibold text-green-600">{{ usersCreated }}</div>
                  </div>
                  <div class="col-6">
                    <div class="text-sm text-600">Usuarios actualizados</div>
                    <div class="text-lg font-semibold text-blue-600">{{ usersUpdated }}</div>
                  </div>
                </div>
              </div>

              <div *ngIf="errors > 0 && errorMessages.length > 0" class="mt-3">
                <p-accordion>
                  <p-accordionTab>
                    <ng-template pTemplate="header">
                      <span class="flex align-items-center gap-2">
                        <i class="pi pi-exclamation-triangle text-orange-500"></i>
                        <span class="font-semibold">Ver errores ({{ errorMessages.length }})</span>
                      </span>
                    </ng-template>
                    <ul class="list-none p-0 m-0">
                      <li *ngFor="let error of errorMessages" class="text-sm text-red-600 mb-2">
                        <i class="pi pi-times-circle mr-2"></i>
                        {{ error }}
                      </li>
                    </ul>
                  </p-accordionTab>
                </p-accordion>
              </div>

              <div class="text-center mt-4">
                <p-button label="Sincronizar de nuevo" icon="pi pi-sync" styleClass="p-button-text" (click)="confirmStart($event)" />
              </div>
            </div>

            <!-- Estado: Error -->
            <div *ngIf="status === 'error' || status === 'timeout'" class="text-center py-4">
              <i class="pi pi-exclamation-triangle text-6xl text-red-500 mb-3"></i>
              <p class="text-lg font-semibold mb-2 text-red-600">Error en la sincronización</p>
              <p-message severity="error" [text]="errorMessage" styleClass="w-full" />

              <div class="mt-4">
                <p-button label="Reintentar" icon="pi pi-sync" (click)="confirmStart($event)" />
              </div>
            </div>
          </ng-template>
        </p-card>

        <!-- Formulario: crear / editar -->
        <p-panel *ngIf="showConfigForm"
          [header]="configMode === 'add' ? 'Nueva configuración de conexión' : 'Editar configuración de conexión'">
          <form [formGroup]="configForm">
            <p-tabView>

              <!-- ── Tab: General ── -->
              <p-tabPanel header="General">
                <div class="p-fluid">

                  <div class="field">
                    <label for="syncConfigName">Nombre <span class="text-red-500">*</span></label>
                    <input id="syncConfigName" pInputText formControlName="name" placeholder="Ej. Sincronización JDE - Corpaceros" />
                    <small class="text-red-500"
                      *ngIf="configForm.get('name')?.invalid && configForm.get('name')?.dirty">
                      El nombre es obligatorio
                    </small>
                  </div>

                  <div class="field">
                    <label for="syncConfigDescription">Descripción</label>
                    <textarea id="syncConfigDescription" pInputTextarea formControlName="description" rows="2"
                      placeholder="Descripción opcional"></textarea>
                  </div>

                  <div class="field">
                    <label>Integración activa</label>
                    <div class="flex align-items-center gap-2 mt-1">
                      <p-inputSwitch formControlName="isActive" />
                      <span>{{ configForm.get('isActive')?.value ? 'Activa' : 'Inactiva' }}</span>
                    </div>
                  </div>

                  <div class="field">
                    <label>Sincronización automática</label>
                    <div class="flex align-items-center gap-2 mt-1">
                      <p-inputSwitch formControlName="isAutoSync" />
                      <span>{{ configForm.get('isAutoSync')?.value ? 'Automática' : 'Manual' }}</span>
                    </div>
                    <small class="text-500" *ngIf="configForm.get('isAutoSync')?.value">
                      Si está activo, el orquestador ejecuta esta compañía según el cron de la pestaña Programación.
                    </small>
                    <small class="text-500" *ngIf="!configForm.get('isAutoSync')?.value">
                      No se sincronizará automáticamente. Solo se puede sincronizar con el botón "Iniciar sincronización".
                    </small>
                  </div>

                </div>
              </p-tabPanel>

              <!-- ── Tab: Programación ── -->
              <p-tabPanel header="Programación">
                <div class="p-fluid">
                  <div class="field">
                    <label for="syncConfigCron">Expresión cron (6 campos)</label>
                    <input id="syncConfigCron" pInputText formControlName="cronExpression"
                      [placeholder]="defaultCronHint" />
                    <small class="text-500">
                      Formato: seg min hora día mes día-semana. Vacío usa {{ defaultCronHint }} (23:00).
                    </small>
                  </div>
                  <div class="field">
                    <label for="syncConfigTz">Zona horaria</label>
                    <p-dropdown inputId="syncConfigTz" formControlName="timeZoneId"
                      [options]="timeZoneOptions" optionLabel="label" optionValue="value"
                      appendTo="body" styleClass="w-full" />
                  </div>
                </div>
              </p-tabPanel>

              <!-- ── Tab: Endpoint ── -->
              <p-tabPanel header="Endpoint">
                <div class="p-fluid">

                  <div class="field">
                    <label for="syncConfigUrl">URL de la API de empleados <span class="text-red-500">*</span></label>
                    <input id="syncConfigUrl" pInputText formControlName="employeesApiUrl"
                      placeholder="https://api.jde.cliente.com/empleados" />
                    <small class="text-red-500"
                      *ngIf="configForm.get('employeesApiUrl')?.invalid && configForm.get('employeesApiUrl')?.dirty">
                      Ingrese una URL válida (https://...)
                    </small>
                  </div>

                  <div class="formgrid grid">
                    <div class="field col-6">
                      <label for="syncConfigTimeout">Timeout (segundos)</label>
                      <p-inputNumber inputId="syncConfigTimeout" formControlName="timeoutSeconds" [min]="1" [max]="300" styleClass="w-full" />
                    </div>
                    <div class="field col-6">
                      <label for="syncConfigRetries">Máximo de reintentos</label>
                      <p-inputNumber inputId="syncConfigRetries" formControlName="maxRetries" [min]="0" [max]="10" styleClass="w-full" />
                    </div>
                  </div>

                </div>
              </p-tabPanel>

              <!-- ── Tab: Autenticación ── -->
              <p-tabPanel header="Autenticación">
                <div class="p-fluid">

                  <p-message
                    *ngIf="configMode === 'edit' && hasPasswordConfigured"
                    severity="info"
                    text="Contraseña configurada. Ingresa una nueva para reemplazarla."
                    styleClass="mb-3 w-full" />

                  <div class="field">
                    <label for="syncConfigUsername">Usuario <span class="text-red-500">*</span></label>
                    <input id="syncConfigUsername" pInputText formControlName="username" placeholder="usuario" />
                    <small class="text-red-500"
                      *ngIf="configForm.get('username')?.invalid && configForm.get('username')?.dirty">
                      El usuario es obligatorio
                    </small>
                  </div>

                  <div class="field">
                    <label for="syncConfigPassword">
                      Contraseña <span class="text-red-500" *ngIf="!configId">*</span>
                    </label>
                    <input id="syncConfigPassword" pInputText type="password" formControlName="password" placeholder="••••••••" />
                    <small class="text-red-500"
                      *ngIf="configForm.get('password')?.invalid && configForm.get('password')?.dirty">
                      La contraseña es obligatoria
                    </small>
                  </div>

                </div>
              </p-tabPanel>

              <!-- ── Tab: Payload Template ── -->
              <p-tabPanel header="Payload Template">
                <div class="grid">

                  <!-- Editor JSON -->
                  <div class="col-12 md:col-7 pr-3">
                    <label class="font-medium">JSON Template</label>
                    <small class="block text-500 mb-2">
                      Si dejas el template vacío se usa el body fijo JDE. Si lo defines, se resuelven
                      <code class="text-primary">{{ '{{campo}}' }}</code> al sincronizar
                      (<code>Identificacion_Empleado</code> / <code>Tipo_Ejecucion</code>).
                    </small>
                    <textarea
                      pInputTextarea
                      formControlName="payloadTemplate"
                      [rows]="22"
                      class="w-full"
                      style="font-family: monospace; font-size: 0.8rem; resize: vertical;"
                      [attr.placeholder]="templatePlaceholder">
                    </textarea>
                  </div>

                  <!-- Panel de placeholders -->
                  <div class="col-12 md:col-5 pl-2">
                    <label class="font-medium">Placeholders disponibles</label>
                    <small class="block text-500 mb-2">Haz clic para copiar</small>

                    <div *ngIf="placeholdersLoading" class="flex justify-content-center p-4">
                      <p-progressSpinner strokeWidth="4" [style]="{ width: '28px', height: '28px' }" />
                    </div>

                    <div *ngIf="!placeholdersLoading" class="overflow-auto border-round border-1 surface-border"
                      style="max-height: 430px;">
                      <div
                        *ngFor="let ph of availablePlaceholders"
                        class="flex align-items-center justify-content-between px-3 py-2 border-bottom-1 surface-border hover:surface-100 cursor-pointer"
                        (click)="copyPlaceholder(ph.key)">
                        <span class="text-sm text-800">{{ ph.label }}</span>
                        <code class="text-primary text-xs font-bold white-space-nowrap ml-2">{{ ph.key }}</code>
                      </div>
                      <div *ngIf="availablePlaceholders.length === 0"
                        class="p-3 text-center text-400 text-sm">
                        No hay placeholders disponibles
                      </div>
                    </div>
                  </div>

                </div>
              </p-tabPanel>

            </p-tabView>
          </form>

          <div class="flex justify-content-between mt-1">
            <p-button label="Cancelar" styleClass="p-button-rounded p-button-danger" (click)="closeConfigForm()" />
            <div class="flex gap-2">
              <p-button
                label="Probar conexión"
                icon="pi pi-bolt"
                styleClass="p-button-outlined p-button-secondary"
                [loading]="testingConnection"
                (click)="testConnection()" />
              <p-button
                label="Guardar"
                icon="pi pi-save"
                styleClass="p-button-rounded"
                [disabled]="configForm.invalid"
                (click)="saveConfig()" />
            </div>
          </div>
        </p-panel>

      </div>


    </p-tabPanel>

    <!-- ════════ Tab 2: Historial ════════ -->
    <p-tabPanel header="Historial de Sincronizaciones">
      <!-- Filtros -->
      <form [formGroup]="filterForm">
        <div class="grid mb-3 align-items-end">
          <div class="col-12 md:col-3">
            <label class="block text-sm mb-1">Estado</label>
            <p-dropdown formControlName="status" [options]="statusOptions"
              optionLabel="label" optionValue="value" placeholder="Todos" [showClear]="true"
              appendTo="body" styleClass="w-full" />
          </div>
          <div class="col-12 md:col-3">
            <label class="block text-sm mb-1">Desde</label>
            <p-calendar formControlName="dateFrom" placeholder="dd/mm/aaaa"
              dateFormat="dd/mm/yy" appendTo="body" styleClass="w-full" />
          </div>
          <div class="col-12 md:col-3">
            <label class="block text-sm mb-1">Hasta</label>
            <p-calendar formControlName="dateTo" placeholder="dd/mm/aaaa"
              dateFormat="dd/mm/yy" appendTo="body" styleClass="w-full" />
          </div>
          <div class="col-12 md:col-3 flex gap-2">
            <button pButton pRipple icon="pi pi-search" class="p-button-sm"
              pTooltip="Buscar" (click)="loadLogs(1)"></button>
            <button pButton pRipple icon="pi pi-times" class="p-button-sm p-button-outlined"
              pTooltip="Limpiar filtros" (click)="clearLogFilters()"></button>
          </div>
        </div>
      </form>

      <!-- Tabla historial -->
      <p-table
        [value]="logs"
        [loading]="logsLoading"
        [lazy]="true"
        (onLazyLoad)="onLogPageChange($event)"
        [paginator]="true"
        [rows]="logPageSize"
        [(first)]="logFirst"
        [totalRecords]="totalLogs"
        [showCurrentPageReport]="true"
        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
        [rowsPerPageOptions]="[20, 50, 100]"
        styleClass="p-datatable-sm p-datatable-striped"
        emptyMessage="No hay registros para los filtros seleccionados.">

        <ng-template pTemplate="header">
          <tr>
            <th>Fecha</th>
            <th>Estado</th>
            <th>Total recibidos</th>
            <th>Creados</th>
            <th>Actualizados</th>
            <th>Errores</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-log>
          <tr>
            <td>{{ log.startedAt | date: 'dd/MM/yyyy HH:mm' }}</td>
            <td>
              <p-tag
                [severity]="getStatusSeverity(log.status)"
                [value]="getStatusLabel(log.status)" />
            </td>
            <td>{{ log.totalEmployeesReceived }}</td>
            <td class="text-green-600 font-semibold">{{ log.usersCreated }}</td>
            <td class="text-blue-600 font-semibold">{{ log.usersUpdated }}</td>
            <td [class.text-red-600]="log.errors > 0" [class.font-semibold]="log.errors > 0">{{ log.errors }}</td>
          </tr>
        </ng-template>

      </p-table>
    </p-tabPanel>

  </p-tabView>
</p-card>
